/**
 * AuthCallback — handles email verification link clicks.
 *
 * URL: /auth/callback?code=XXXXXXXX&sessionId=YYYYY
 *
 * Calls verify API with session_id + code (no sessionToken needed — the
 * backend's service token has sufficient permissions).  Auto-logs in.
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyOtp, type AuthUser } from "./AuthProvider";

interface AuthCallbackProps {
  onSuccess: (token: string, user: AuthUser) => void;
}

export function AuthCallback({ onSuccess }: AuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "error">("verifying");
  const [error, setError] = useState("");

  const code = searchParams.get("code") || "";
  const sessionId = searchParams.get("sessionId") || "";

  useEffect(() => {
    if (!code || !sessionId) {
      setStatus("error");
      setError("Link non valido — parametri mancanti.");
      return;
    }

    let cancelled = false;
    verifyOtp(sessionId, "", code)
      .then((result) => {
        if (!cancelled) {
          onSuccess(result.token, result.user);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setStatus("error");
          setError((err as Error).message);
        }
      });

    return () => { cancelled = true; };
  }, [code, sessionId, onSuccess, navigate]);

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", fontFamily: "var(--font-family)",
      background: "linear-gradient(160deg, var(--indaco-gray-50) 0%, var(--indaco-gray-200) 100%)",
    }}>
      <div style={{
        background: "var(--indaco-white)", borderRadius: "var(--border-radius-xl)",
        boxShadow: "0 4px 24px rgba(0,40,85,0.10)", padding: "2.5rem 2rem",
        maxWidth: 420, width: "100%", textAlign: "center",
      }}>
        {status === "verifying" && (
          <>
            <div className="spinner spinner-lg" style={{ margin: "0 auto 1rem" }} />
            <p>Accesso in corso…</p>
          </>
        )}
        {status === "error" && (
          <>
            <p style={{ color: "var(--indaco-danger)" }}>⚠ {error}</p>
            <button className="btn btn-primary" style={{ marginTop: "1rem" }}
              onClick={() => navigate("/", { replace: true })}>
              Torna al login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
